// @ts-ignore
import Leite from "leite";
import { sampleSize } from "lodash-es";
import {
  booleanOptions,
  closingLocationOptions,
  clothingSizeOptions,
  danceOptions,
  disabilityTypeOptions,
  eyesColorOptions,
  genderIdentityOptions,
  hairColorOptions,
  hairTypeOptions,
  kinshipOptions,
  languageProficiencyOptions,
  languagesOptions,
  musicOptions,
  physicalCharacteristicOptions,
  shoeSizeOptions,
  skinColorOptions,
  sportsOptions,
} from "../components/features/talent/forms/FormTalent/formValues";

const leite = new Leite();

// CEPs válidos de São Paulo e Rio de Janeiro
const validCEPs = [
  "01310-100", // Av Paulista, SP
  "01452-002", // Shopping Iguatemi, SP
  "04543-011", // Vila Olímpia, SP
  "05426-100", // Pinheiros, SP
  "22290-030", // Copacabana, RJ
  "22410-003", // Ipanema, RJ
  "20040-020", // Centro, RJ
  "01310-200", // Bela Vista, SP
];

// Nomes de ruas brasileiras comuns
const streetNames = [
  "Rua das Flores",
  "Avenida Brasil",
  "Rua São João",
  "Avenida Paulista",
  "Rua Augusta",
  "Rua Oscar Freire",
  "Alameda Santos",
  "Rua Vergueiro",
  "Avenida Brigadeiro Faria Lima",
  "Rua Consolação",
];

// Bairros comuns
const neighborhoods = [
  "Vila Mariana",
  "Pinheiros",
  "Moema",
  "Jardins",
  "Vila Olímpia",
  "Itaim Bibi",
  "Perdizes",
  "Higienópolis",
  "Santana",
  "Mooca",
];

// Função para gerar data de nascimento
function generateBirthDate(isMinor: boolean): string {
  const today = new Date();
  const year = today.getFullYear();

  if (isMinor) {
    // Entre 5 e 17 anos
    const age = Math.floor(Math.random() * 13) + 5;
    const birthYear = year - age;
    const month = Math.floor(Math.random() * 12) + 1;
    const day = Math.floor(Math.random() * 28) + 1;
    // Retornar no formato ISO YYYY-MM-DD
    return `${birthYear}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
  } else {
    // Entre 18 e 35 anos
    const age = Math.floor(Math.random() * 18) + 18;
    const birthYear = year - age;
    const month = Math.floor(Math.random() * 12) + 1;
    const day = Math.floor(Math.random() * 28) + 1;
    // Retornar no formato ISO YYYY-MM-DD
    return `${birthYear}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
  }
}

// Função para selecionar valor aleatório de array
function randomFromArray<T>(arr: Array<{ label: string; value: T }>): T {
  return arr[Math.floor(Math.random() * arr.length)].value;
}

// Função para selecionar múltiplos valores aleatórios de array (para campos multiselect)
function randomOptionsFromArray<T>(arr: Array<{ label: string; value: T }>, maxOptions: number = 3): T[] {
  return sampleSize(arr, Math.floor(Math.random() * (maxOptions + 1))).map((option) => option.value);
}

// Função para gerar telefone brasileiro
function generatePhone(): string {
  const ddd = ["11", "21", "31", "41", "51", "61", "71", "81", "91"][Math.floor(Math.random() * 9)];
  const prefix = Math.floor(Math.random() * 9000) + 1000; // 4 dígitos
  const suffix = Math.floor(Math.random() * 9000) + 1000; // 4 dígitos
  return `${ddd}9${prefix}${suffix}`; // Total: 11 dígitos (DD + 9 + XXXX + XXXX)
}

// Função para gerar email
function generateEmail(name: string): string {
  const providers = ["gmail.com", "outlook.com", "yahoo.com.br", "hotmail.com", "uol.com.br"];
  // Remove acentos e caracteres especiais
  const cleanName = name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove acentos
    .replace(/[^a-z0-9]/g, ".") // Substitui caracteres especiais por ponto
    .replace(/\.+/g, ".") // Remove pontos duplicados
    .replace(/^\.|\.$/g, ""); // Remove pontos no início e fim
  const randomNum = Math.floor(Math.random() * 1000);
  const provider = providers[Math.floor(Math.random() * providers.length)];
  return `${cleanName}${randomNum}@${provider}`;
}

// Função principal para gerar dados do talento
export function generateMockTalentData() {
  const isMinor = Math.random() > 0.5;
  const hasDisability = Math.random() > 0.8; // 20% de chance
  const hasSibling = Math.random() > 0.5;
  const isTwin = hasSibling && Math.random() > 0.7; // 30% chance se tiver irmão
  const hasPreviousWork = Math.random() > 0.6;
  const hasAnotherAgency = Math.random() > 0.8;
  const hasPhotoMaterial = Math.random() > 0.5;

  const fullName = `${leite.pessoa.nome()} ${leite.pessoa.sobrenome()}`;
  const firstName = fullName.split(" ")[0];

  // Endereço principal
  const selectedCEP = validCEPs[Math.floor(Math.random() * validCEPs.length)];
  const mainAddress = {
    zipCode: selectedCEP,
    street: streetNames[Math.floor(Math.random() * streetNames.length)],
    number: Math.floor(Math.random() * 2000) + 1 + "",
    complement: Math.random() > 0.5 ? `Apto ${Math.floor(Math.random() * 200) + 1}` : "",
    neighborhood: neighborhoods[Math.floor(Math.random() * neighborhoods.length)],
    city: selectedCEP.startsWith("0") ? "São Paulo" : "Rio de Janeiro",
    state: selectedCEP.startsWith("0") ? "SP" : "RJ",
  };

  // Gerar dados do responsável - sempre incluir pelo menos um para testes
  // Se for menor, é obrigatório. Se for adulto, incluímos mesmo assim para teste
  const sameAddressAsTalent = Math.random() > 0.3; // 70% mora no mesmo endereço
  const responsibles =
    isMinor || true
      ? [
          {
            fullName: `${leite.pessoa.nome()} ${leite.pessoa.sobrenome()}`,
            kinship: randomFromArray(kinshipOptions),
            phone: generatePhone(),
            secondaryPhone: generatePhone(),
            email: generateEmail(`${leite.pessoa.nome()}`),
            birthday: generateBirthDate(false), // Responsável é sempre adulto
            rg: leite.pessoa.rg(),
            cpf: leite.pessoa.cpf(),
            sameAddressAsTalent,
            address: sameAddressAsTalent
              ? mainAddress
              : {
                  zipCode: validCEPs[Math.floor(Math.random() * validCEPs.length)],
                  street: streetNames[Math.floor(Math.random() * streetNames.length)],
                  number: Math.floor(Math.random() * 2000) + 1 + "",
                  complement: "",
                  neighborhood: neighborhoods[Math.floor(Math.random() * neighborhoods.length)],
                  city: "São Paulo",
                  state: "SP",
                },
          },
        ]
      : [];

  // Gerar idiomas (1 a 3 idiomas)
  const numLanguages = Math.floor(Math.random() * 3) + 1;
  const languages = [];
  const usedLanguages = new Set();

  for (let i = 0; i < numLanguages; i++) {
    let language;
    do {
      language = randomFromArray(languagesOptions);
    } while (usedLanguages.has(language));

    usedLanguages.add(language);
    languages.push({
      name: language,
      proficiencyLevel: randomFromArray(languageProficiencyOptions),
    });
  }

  // Gerar habilidades esportivas - garantir que não seja 'OTHER'
  const sportsSkills = Math.random() > 0.5 ? [randomFromArray(sportsOptions.filter((s) => s.value !== "OTHER"))] : [];

  // Gerar habilidades musicais
  const musicSkills = Math.random() > 0.5 ? [randomFromArray(musicOptions.filter((m) => m.value !== "OTHER"))] : [];

  // Gerar habilidades de dança
  const danceSkills = Math.random() > 0.5 ? [randomFromArray(danceOptions.filter((d) => d.value !== "OTHER"))] : [];

  const mockData = {
    profilePhotoUrl: "https://picsum.photos/300/300", // Lorem Picsum - serviço gratuito de fotos aleatórias
    closingLocation: randomFromArray(closingLocationOptions),
    nationality: "BRAZILIAN", // Sempre brasileiro pois estamos usando dados brasileiros
    fullName,
    artisticName: firstName,
    birthday: generateBirthDate(isMinor),
    email: generateEmail(fullName),
    rg: leite.pessoa.rg(),
    cpf: leite.pessoa.cpf(),
    genderIdentity: randomFromArray(genderIdentityOptions),
    genderIdentityDetails: null, // Should be null when not "OTHER"
    hasBiologicalSibling: hasSibling,
    isTwinSibling: isTwin,
    hasDisability,
    disabilityType: hasDisability ? [randomFromArray(disabilityTypeOptions)] : null,
    hasPreviousWorkExperience: hasPreviousWork,
    hasDRT: !isMinor && hasPreviousWork, // DRT apenas para adultos com experiência
    isRepresentedByAnotherAgency: hasAnotherAgency,
    hasPhotoMaterial: hasPhotoMaterial,
    address: mainAddress,
    phone: generatePhone(),
    secondaryPhone: generatePhone(),
    socialMedias: [], // Deixar vazio por enquanto para evitar problemas de validação

    // Características físicas
    characteristic: {
      eyeColor: randomFromArray(eyesColorOptions),
      naturalHairColor: randomFromArray(hairColorOptions),
      currentHairColor: randomFromArray(hairColorOptions),
      naturalHairType: randomFromArray(hairTypeOptions),
      currentHairType: randomFromArray(hairTypeOptions),
      skinColor: randomFromArray(skinColorOptions),
      physicalFeatures: randomOptionsFromArray(physicalCharacteristicOptions),
      hasTattoo: randomFromArray(booleanOptions),
    },

    // Medidas
    measurements: {
      height: Math.floor(Math.random() * 60) + 140, // Entre 140 e 200cm
      shoeSize: randomOptionsFromArray(shoeSizeOptions),
      clothingSize: randomOptionsFromArray(clothingSizeOptions),
    },

    // Habilidades
    abilities: {
      languages,
      sports: sportsSkills,
      othersSports: "",
      music: musicSkills,
      othersMusic: "",
      dance: danceSkills,
      othersDance: "",
    },

    responsibles,

    // Campos adicionais do backend
    observation: null, // Campo opcional - backend espera null ao invés de string vazia
    archived: false, // Campo obrigatório
  };

  return mockData;
}

// Função para preencher o formulário com dados mock
export function fillFormWithMockData(setValue: any) {
  const mockData = generateMockTalentData();

  // Função auxiliar para percorrer e setar valores aninhados
  const setNestedValues = (obj: any, prefix = "") => {
    Object.entries(obj).forEach(([key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;

      if (value === null || value === undefined) {
        return;
      }

      if (Array.isArray(value)) {
        if (key === "socialMedias" || key === "languages") {
          // Para arrays de objetos complexos
          setValue(fullKey, value);
        } else if (key === "responsibles") {
          // Para responsáveis
          value.forEach((responsible, index) => {
            Object.entries(responsible).forEach(([respKey, respValue]) => {
              if (respKey === "address" && typeof respValue === "object" && respValue !== null) {
                Object.entries(respValue).forEach(([addrKey, addrValue]) => {
                  setValue(`${fullKey}.${index}.address.${addrKey}`, addrValue);
                });
              } else {
                setValue(`${fullKey}.${index}.${respKey}`, respValue);
              }
            });
          });
        } else {
          // Para outros arrays
          setValue(fullKey, value);
        }
      } else if (typeof value === "object") {
        // Para objetos aninhados
        setNestedValues(value, fullKey);
      } else {
        // Para valores primitivos
        setValue(fullKey, value);
      }
    });
  };

  setNestedValues(mockData);

  return mockData;
}
